import React from 'react';

import { useAppContext } from 'src/components/app-context';
import { PageContent } from 'src/components/page';
import type { Article, ArticleQuery } from 'src/types';
import { FeaturedArticle } from '../../components/featured-article';
import CampaignBreadcrumb from '../campaign/components/breadcrumb';

import { Breadcrumbs } from '@prisjakt/ui/breadcrumbs';
import { Link } from 'react-router-dom';
import Loader from 'src/components/loader';
import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';
import { useMatchedRoute } from 'src/hooks/use-matched-route';
import { useArticlePageQuery } from './queries/use-article-page-query';
import { ArticleWrapper, BreadCrumbWrapper, Wrapper } from './styles';

type RequiredNotNull<T> = Required<{ [P in keyof T]: NonNullable<T[P]> }>;

const ArticlePageLoader = () => {
  const route = useMatchedRoute();

  const { data, isLoading, isSuccess } = useArticlePageQuery(route?.variables);

  if (!isLoading && !isSuccess) {
    return null;
  }

  return (
    <PageContent withAds>
      {isLoading ? <Loader /> : null}
      {isSuccess && data.article ? (
        <ArticlePage article={data.article} />
      ) : null}
    </PageContent>
  );
};

const ArticlePage = ({
  article,
}: RequiredNotNull<Pick<ArticleQuery, 'article'>>) => {
  const { t } = useAppContext();

  function renderBreadcrumb() {
    const { campaign } = article.related ?? {};

    if (campaign) {
      return <CampaignBreadcrumb campaign={campaign} article={article} />;
    }

    return (
      <BreadCrumbWrapper>
        <Breadcrumbs
          containerProps={{
            onClick: (nextPage: string) => {
              trackInternalNavigationClick({
                scope: InternalNavigationScope.BREADCRUMB,
                nextPage,
              });
            },
          }}
        >
          <Link to="/">{t('common', 'breadcrumb.home')}</Link>
          <span>{article.title}</span>
        </Breadcrumbs>
      </BreadCrumbWrapper>
    );
  }

  return (
    <ArticleWrapper>
      {renderBreadcrumb()}
      <Wrapper>
        <FeaturedArticle article={article as Article} embedded={false} />
      </Wrapper>
    </ArticleWrapper>
  );
};

export default ArticlePageLoader;
